// src/pages/reservations/ReservationTabs.tsx
import { ArrowRightIcon, Pencil1Icon, TrashIcon } from "@radix-ui/react-icons";
import { Card, Heading, Tabs } from "@radix-ui/themes";
import { useState } from "react";
import toast from "react-hot-toast";
import { useMutation, useQueryClient } from "react-query";
import { useNavigate } from "react-router-dom";
import { ConfirmAlertDialog } from "../../../components/common/ConfirmAlertDialog";
import { Lists } from "../../../components/common/Lists";
import { SchoolCalendar } from "../../../components/common/SchoolCalendar";
import TableComponent from "../../../components/common/Tables";
import { useAppDispatch } from "../../../hooks/hooks";
import { useGetReservationsQuery } from "../../../hooks/library/useGetReservationsQuery";
import { setReservationData } from "../../../redux/slices/library/reservation";
import { setUserData } from "../../../redux/slices/users/user";
import { routes } from "../../../routes";
import { createLoan } from "../../../services/library/loan";
import { updateReservation } from "../../../services/library/reservation";
import { getUserById } from "../../../services/user/getUsers";
import { LoanType, ReservationType } from "../../../types/Library";
import { key_reservations } from "../../../utils/constants";

type ActiveSegmentTab = "Calendar" | "List";

const columnNamesMap: { [key: string]: string } = {
    bookTitle: "Livro",
    userFullname: "Utilizador",
    reservationDate: "Data da Reserva",
    startDate: "Data de Início",
    endDate: "Data de Término",
};

export const Reservations = () => {
    const [activeSegment, setActiveSegment] = useState<ActiveSegmentTab>("Calendar");
    const [currentPage, setCurrentPage] = useState(1);
    const [deleteItem, setDeleteItem] = useState<ReservationType | null>(null);
    const [confirmLoan, setConfirmLoan] = useState<ReservationType | null>(null);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const queryClient = useQueryClient();

    const { data: reservationsData, status, isLoading } = useGetReservationsQuery({ PageNumber: currentPage, PageSize: 10 });
    const mutationCreateLoan = useMutation({ mutationFn: createLoan, mutationKey: "createLoan" });
    const mutationUpdate = useMutation(updateReservation, {
        onSuccess: () => {
            queryClient.invalidateQueries(key_reservations);
            toast.success("Reserva actualizada");
        },
        onError: () => {
            toast.error("Erro ao actualizar a reserva.");
        },
    });

    const handlePageChange = (page: number) => setCurrentPage(page);

    const handleClickCreate = () => navigate(routes.createReservation);

    const handleDelete = (reservation: ReservationType) => {
        if (reservation.loanDate) {
            toast.error("Tem um empréstimo associado; não é possível editar.");
            return;
        }
        reservation.status = false;
        mutationUpdate.mutateAsync(reservation);
    };

    const openDeleteDialog = (reservation: ReservationType) => setDeleteItem(reservation);

    const confirmDelete = async () => {
        if (deleteItem) await handleDelete(deleteItem);
    };


    const handleEdit = async (reservation: ReservationType) => {
        try {
            if (reservation.loanDate) {
                toast.error("Tem um empréstimo associado; não é possível editar.");
                return;
            }
            const userData = await getUserById(reservation.idUser);
            dispatch(setUserData(userData));
            dispatch(setReservationData(reservation));
            navigate(routes.updateReservation);

        } catch (error) {
            toast.error("Não foi possível carregar os dados.");
        }
    };
    const openCreateLoanDialog = (reservation: ReservationType) => setConfirmLoan(reservation);

    const handleLoan = async (reservation: ReservationType) => {
        try {
            if (reservation.loanDate) {
                toast.error("Possui empréstimo associado");
                return;
            }
            reservation.loanDate = new Date().toISOString();
            const loan: LoanType = {
                idBook: reservation.idBook,
                idUser: reservation.idUser,
                loanDate: reservation.loanDate,
                dueDate: reservation.endDate,
                userFullname: reservation.userFullname,
            }
            await mutationCreateLoan.mutateAsync(loan);
            await mutationUpdate.mutateAsync(reservation)

        } catch (error) {
            toast.error("Não foi possível carregar os dados.");
        }
    };
    const handleConfirmLoan = async () => {
        if (confirmLoan) await handleLoan(confirmLoan);
    };
    const options = reservationsData?.items.map((reservation: ReservationType) => [
        {
            label: "Editar",
            onClick: () => handleEdit(reservation),
            icon: <Pencil1Icon />,
        },
        {
            label: "Entregar reserva",
            onClick: () => openCreateLoanDialog(reservation),
            icon: <ArrowRightIcon />,
        },
        {
            label: "Eliminar",
            onClick: () => openDeleteDialog(reservation),
            icon: <TrashIcon />,
        },
    ]) || [];

    const events = reservationsData?.items
        .filter(r => !r.loanDate)
        .map((reservation) => ({
            title: `${reservation.userFullname} ${reservation?.bookTitle}`,
            start: new Date(reservation.startDate),
            end: new Date(reservation.endDate),
        })) || [];

    return (
        <Tabs.Root value={activeSegment} onValueChange={(tabToNavigate) => setActiveSegment(tabToNavigate as ActiveSegmentTab)}>
            <Tabs.List>
                <Tabs.Trigger value="Calendar">
                    Calendário
                </Tabs.Trigger>
                <Tabs.Trigger value="List">
                    Listagem de Reservas
                </Tabs.Trigger>
            </Tabs.List>

            <Tabs.Content value="Calendar">
                <Card size="3" mt="4">
                    <Heading size="3" mb="4">
                        Calendário de Reservas
                    </Heading>
                    <SchoolCalendar events={events} />
                </Card>
            </Tabs.Content>

            <Tabs.Content value="List">
                <Lists
                    status={status}
                    TableComponent={() => (
                        <TableComponent
                            columns={Object.keys(columnNamesMap)}
                            items={reservationsData?.items}
                            options={options}
                            columnNamesMap={columnNamesMap}
                            totalPages={reservationsData?.totalPages || 1}
                            currentPage={currentPage}
                            onPageChange={handlePageChange}
                            emptyMessage="Ainda não existem reservas registadas."
                        />
                    )}
                    handleClickCreate={handleClickCreate}
                />
                <ConfirmAlertDialog
                    open={!!deleteItem}
                    onOpenChange={(open) => !open && setDeleteItem(null)}
                    title="Esta operação é irreversível"
                    description="A reserva deixará de estar disponível. Pretende continuar?"
                    confirmLabel="Eliminar"
                    item={deleteItem}
                    onConfirm={confirmDelete}
                    onCancel={() => setDeleteItem(null)}
                />
                <ConfirmAlertDialog
                    open={!!confirmLoan}
                    onOpenChange={(open) => !open && setConfirmLoan(null)}
                    title="Esta operação é irreversível"
                    description="A reserva será convertida num empréstimo. Pretende continuar?"
                    confirmLabel="Entregar reserva"
                    confirmColor="green"
                    item={confirmLoan}
                    onConfirm={handleConfirmLoan}
                    onCancel={() => setConfirmLoan(null)}
                />
            </Tabs.Content>
        </Tabs.Root>
    );
};
